import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";

import { COMPETITORS_BASE } from "@/components/competitors/routes";

import {
  MOBILE_ADMIN_ENTRIES,
  MOBILE_CALENDAR_PATH,
  MOBILE_MORE_ENTRIES,
  MOBILE_MORE_PATH,
} from "./mobileModules";

/**
 * Secondary pages: everything reachable from the "Más" hub (and the
 * account window's admin entries, plus the calendar page, linked from the
 * home). Their top bar shows a back chevron in
 * place of the avatar; the root tabs (home, tasks, deals) and the hub keep
 * the avatar.
 */
const SECONDARY_PREFIXES: readonly string[] = [
  ...MOBILE_MORE_ENTRIES,
  ...MOBILE_ADMIN_ENTRIES,
  { to: MOBILE_CALENDAR_PATH },
  { to: "/profile" },
  { to: "/events" },
].map((entry) => entry.to);

/**
 * Pages that still draw a back button in their own header: a report (deal)
 * opened from the reports list. No second one in the top bar. Contact and
 * company pages (lists, details, notes, forms) use the top bar's glass
 * chevron like every other secondary page.
 */
const OWN_BACK_PATTERNS: readonly RegExp[] = [/^\/informes\/.+/];

/**
 * Where Back goes on a cold start (no earlier page in the app): a record's
 * page goes up to its list, anything else to the hub.
 */
const PARENT_LISTS: readonly string[] = [
  "/contacts",
  "/companies",
  COMPETITORS_BASE,
];

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Whether the mobile top bar shows the back chevron on `pathname`. */
export const hasTopBarBack = (pathname: string): boolean =>
  SECONDARY_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix)) &&
  !OWN_BACK_PATTERNS.some((pattern) => pattern.test(pathname));

/** Where Back goes when the app has no earlier page (a deep link). */
export const MOBILE_BACK_FALLBACK = MOBILE_MORE_PATH;

/** The cold-start Back target of `pathname` (see PARENT_LISTS). */
export const mobileBackFallback = (pathname: string): string =>
  PARENT_LISTS.find(
    (list) => pathname !== list && matchesPrefix(pathname, list),
  ) ?? MOBILE_BACK_FALLBACK;

/**
 * Back from a secondary page: the previous page when the app has one,
 * otherwise its list or the hub (a cold start on a deep link never strands
 * the user).
 */
export const useMobileBack = (): (() => void) => {
  const navigate = useNavigate();
  const { key, pathname } = useLocation();
  return useCallback(() => {
    if (key !== "default") {
      navigate(-1);
    } else {
      navigate(mobileBackFallback(pathname), { replace: true });
    }
  }, [key, pathname, navigate]);
};
